import type { GameStateManager } from '../game/GameState';
import type { GameData, GameSpeed } from '../game/types';

export class ControlsModal {
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData,
    private onSpeedRestore?: (speed: GameSpeed) => void
  ) {
    this.setup();
  }

  private setup(): void {
    this.container.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('modal-backdrop')) {
        this.hide();
      }
    });
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.container.classList.contains('hidden')) return;

    if (event.code === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    }
  }

  show(): void {
    const hotkeys = this.gameData.hotkeys;

    this.container.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="modal-content controls-modal" tabindex="0">
        <div class="modal-header">
          <h2>Controls</h2>
          <button class="modal-close" data-action="close">×</button>
        </div>
        <div class="modal-body">
          ${this.renderSection('Nine-Pad Controls', hotkeys.nine_pad, this.formatNinePad)}
          ${this.renderSection('Navigation', hotkeys.navigation, this.formatStandard)}
          ${this.renderSection('Speed Controls', hotkeys.speed, this.formatStandard)}
          ${this.renderSection('Move Mode', hotkeys.move_mode, this.formatStandard)}
          ${this.renderSection('Modal Controls', hotkeys.modal, this.formatStandard)}
        </div>
        <div class="modal-footer">
          <button class="button-confirm" data-action="close">Close</button>
        </div>
      </div>
    `;

    const closeButton = this.container.querySelector('.modal-close') as HTMLElement;
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide());
    }

    const footerCloseButton = this.container.querySelector('.modal-footer [data-action="close"]') as HTMLElement;
    if (footerCloseButton) {
      footerCloseButton.addEventListener('click', () => this.hide());
    }

    this.container.classList.remove('hidden');
    this.gameState.setPausedByModal(true);
    
    this.keyDownHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
    document.addEventListener('keydown', this.keyDownHandler);
    
    const modalContent = this.container.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      modalContent.focus();
    }
  }

  private renderSection(title: string, data: Record<string, unknown>, formatter: (data: Record<string, unknown>) => string): string {
    if (!data) return '';
    return `
      <div class="controls-section">
        <h3>${title}</h3>
        <div class="controls-list">
          ${formatter(data)}
        </div>
      </div>
    `;
  }

  private formatNinePad(data: Record<string, unknown>): string {
    const rows = ['top_row', 'middle_row', 'bottom_row'];
    const rowLabels = ['Top Row', 'Middle Row', 'Bottom Row'];
    
    return rows.map((rowKey, index) => {
      const row = data[rowKey];
      if (!row || !Array.isArray(row)) return '';
      
      return `
        <div class="controls-row">
          <div class="controls-row-label">${rowLabels[index]}:</div>
          <div class="controls-row-keys">
            ${row.map((item: { code: string; index: number; label: string }) => `
              <div class="control-item">
                <span class="control-key">${item.label}</span>
                <span class="control-description">Index ${item.index}</span>
              </div>
            `).join('')}
          </div>
        </div>
      `;
    }).join('');
  }

  private formatStandard(data: Record<string, unknown>): string {
    return Object.entries(data).map(([key, value]) => {
      const label = key.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      return `
        <div class="control-item">
          <span class="control-key">${value.label || value}</span>
          <span class="control-description">${label}</span>
        </div>
      `;
    }).join('');
  }

  hide(): void {
    this.container.classList.add('hidden');
    this.gameState.setPausedByModal(false);
    const restoredSpeed = this.gameState.restorePauseStateAfterModal();
    if (restoredSpeed !== null && this.onSpeedRestore) {
      this.onSpeedRestore(restoredSpeed);
    }
    
    if (this.keyDownHandler) {
      document.removeEventListener('keydown', this.keyDownHandler);
      this.keyDownHandler = null;
    }
  }

  isOpen(): boolean {
    return !this.container.classList.contains('hidden');
  }
}
